import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, ChevronLeft, ChevronRight, Dog, Loader2, Lock,
  Search, Send, Tags, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { ApiClientError } from "@/api/client";
import { RECORD_TYPE_LABEL } from "@/lib/client360Labels";
import {
  fetchBreeds, fetchBusinessProfile, quickFormSave,
  type BreedRef, type BrandRef, type GateBranch, type QuickFormSavePayload,
} from "@/api/businessProfile";
import { profileTransition, type ProfileVerifyState } from "@/api/client360Profile";
import {
  ANIMAL_TYPES, ASSORTMENT, BUSINESS_MODELS, CHANNEL, COUNT_BAND, DEALER_TYPE,
  DECISION, EXCLUSIVITY, M_LABEL, NETWORK_TYPE, OBLAST, ORDER_FREQ,
  PRICE_POSITIONING, PURCHASING, SALES_CHANNELS, SCALE,
  asArray, asStr, BrandsPicker, Chip, ChipMulti, Field, Select,
} from "@/pages/clients/BusinessProfilePage";

/* §4 MP4 — B2B decision/order contact roles (CClientBusinessProfile enums). */
const DECISION_MAKER_ROLE: Array<[string, string]> = [
  ["owner", "Власник"], ["manager", "Менеджер"], ["buyer", "Закупник"],
  ["accountant", "Бухгалтер"], ["specialist", "Спеціаліст"], ["other", "Інше"], ["unknown", "Не уточнено"],
];
const ORDER_MAKER_ROLE: Array<[string, string]> = [
  ["owner", "Власник"], ["manager", "Менеджер"], ["buyer", "Закупник"],
  ["employee", "Працівник"], ["external", "Зовнішній"], ["other", "Інше"], ["unknown", "Не уточнено"],
];
const POTENTIAL: Array<[string, string]> = [
  ["low", "Низький"], ["medium", "Середній"], ["high", "Високий"], ["strategic", "Стратегічний"],
];

/* v1.4 quick-wins — UI-select allowlists над varchar-колонками (0 міграції,
 * значення = читабельні UA-літерали; legacy free-text зберігається як
 * додаткова опція «(збережене)», ніколи не губиться мовчки — AC-A4R-017). */
const CLUB_REGISTRY: Array<[string, string]> = [
  ["КСУ", "КСУ"], ["FCI", "FCI"], ["WCF", "WCF"], ["TICA", "TICA"], ["FIFe", "FIFe"],
  ["інше", "Інше"], ["немає", "Немає"], ["невідомо", "Невідомо"],
];
const FEEDING_SCHEME: Array<[string, string]> = [
  ["наш бренд", "Наш бренд"], ["конкурентний бренд", "Конкурентний бренд"],
  ["кілька брендів", "Кілька брендів"], ["натуральне", "Натуральне"],
  ["змішане", "Змішане"], ["невідомо", "Невідомо"],
];
/** Конкурентний бренд показується ЛИШЕ для цих значень схеми (AC-A4R-005). */
const FEEDING_COMPETITOR_TRIGGER = new Set(["конкурентний бренд", "кілька брендів"]);
/** Legacy-значення поза allowlist → доклеюємо опцією, щоб не загубити. */
function withLegacy(opts: Array<[string, string]>, value: string): Array<[string, string]> {
  if (!value || opts.some(([v]) => v === value)) return opts;
  return [...opts, [value, `${value} (збережене)`]];
}

/**
 * Profile Edit Wizard v1.1 — EDIT MODE as a right-drawer WIZARD (НЕ page-form).
 *
 * 4 steps: Гілка (confirm) → Обов'язкові (type-aware) → Додаткові → Review/Save.
 * UX-only over the 3.0A-4 quick-form contract: state + gate-mirror + save are the
 * SAME QuickFormSave payload as the legacy page form (model/JSON/lifecycle
 * unchanged). Breeder branch shows NO B2B fields; commercial shows its own §4 set.
 * Draft save always available (MP3: success-state, не закриває мовчки); submit
 * «Подати на перевірку» DISABLED while required missing + hint with CONCRETE
 * fields (MP1). verify = S/A — the wizard never self-verifies.
 *
 * NOT editable here (model-locked, P2 provenance): Розплідник
 * (cCBreederKennelNamePrimary) — read-only on the summary card.
 */
export function ProfileEditWizard({
  open,
  onClose,
  holdingId,
  displayName,
  recordType,
  canSubmit,
  verifyState,
  canWithdraw,
  onWithdraw,
  onSubmitted,
  onOpenClassification,
}: {
  open: boolean;
  onClose: () => void;
  holdingId: string;
  displayName: string;
  recordType: string;
  /** Lifecycle submit allowance (Client360Profile.allowedActions.canSubmit). */
  canSubmit: boolean;
  /** BUG1 — lifecycle state: needs_review → «Вже подано» (+Відкликати), not a role error. */
  verifyState: ProfileVerifyState;
  canWithdraw: boolean;
  onWithdraw: () => void;
  /** Called after a successful submit (drawer already closed). */
  onSubmitted?: () => void;
  onOpenClassification: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const q = useQuery({
    queryKey: ["businessProfile", holdingId],
    queryFn: ({ signal }) => fetchBusinessProfile(holdingId, signal),
    enabled: open && !!holdingId,
    staleTime: 60_000,
  });

  // ── editable state (mirrors the quick-form contract) ──────────────────────
  const [models, setModels] = useState<string[]>([]);
  const [primary, setPrimary] = useState("");
  const [otherNote, setOtherNote] = useState("");
  const [channel, setChannel] = useState("");
  const [override, setOverride] = useState("");
  const [scale, setScale] = useState("");
  const [purchasing, setPurchasing] = useState("");
  const [decision, setDecision] = useState("");
  const [orderFreq, setOrderFreq] = useState("");
  const [animalTypes, setAnimalTypes] = useState<string[]>([]);
  const [breeds, setBreeds] = useState<BreedRef[]>([]);
  const [breedOther, setBreedOther] = useState("");
  const [countBand, setCountBand] = useState("");
  const [networkType, setNetworkType] = useState("");
  const [networkOverride, setNetworkOverride] = useState("");
  const [dealerType, setDealerType] = useState("");
  const [dealerTerritory, setDealerTerritory] = useState<string[]>([]);
  const [representedBrands, setRepresentedBrands] = useState<BrandRef[]>([]);
  const [exclusivity, setExclusivity] = useState("");
  const [ecomShopName, setEcomShopName] = useState("");
  const [salesChannels, setSalesChannels] = useState<string[]>([]);
  const [pricePositioning, setPricePositioning] = useState("");
  const [ecomAssortment, setEcomAssortment] = useState<string[]>([]);
  const [activeOutlets, setActiveOutlets] = useState(0);
  // Wizard-added (still inside the QuickFormSave whitelist):
  const [feedingProgram, setFeedingProgram] = useState("");
  const [certifications, setCertifications] = useState("");
  const [competitorBrand, setCompetitorBrand] = useState("");
  const [managerComment, setManagerComment] = useState("");
  const [potential, setPotential] = useState("");
  const [decisionMakerRole, setDecisionMakerRole] = useState("");
  const [orderMakerRole, setOrderMakerRole] = useState("");
  const [assortmentFocus, setAssortmentFocus] = useState<string[]>([]);

  useEffect(() => {
    const f = q.data?.fields;
    if (!f) return;
    // Seed-normalize: backend rejects primary ∉ models («Основна модель не з
    // обраних»). Classification can set primary while cCBusinessModels is still
    // empty — include the primary's chip so the state is consistent + saveable.
    const seededModels = asArray(f.cCBusinessModels);
    const seededPrimary = asStr(f.cCPrimaryBusinessModel);
    setModels(seededPrimary && !seededModels.includes(seededPrimary)
      ? [...seededModels, seededPrimary]
      : seededModels);
    setPrimary(seededPrimary);
    setOtherNote(asStr(f.cCBusinessModelOtherNote));
    setChannel(asStr(f.cCPrimaryChannel));
    setOverride(asStr(f.cCManagerReviewOverrideReason));
    setScale(asStr(f.cCBusinessScale));
    setPurchasing(asStr(f.cCPurchasingModel));
    setDecision(asStr(f.cCDecisionModel));
    setOrderFreq(asStr(f.cCOrderFrequency));
    setAnimalTypes(asArray(f.cCBreederAnimalTypes));
    setBreeds(Array.isArray(f.cCBreederBreeds) ? (f.cCBreederBreeds as BreedRef[]) : []);
    setBreedOther(asStr(f.cCBreederBreedOtherText));
    setCountBand(asStr(f.cCBreederAnimalCountBand));
    setNetworkType(asStr(f.cCNetworkType));
    setNetworkOverride(asStr(f.cCNetworkSizeOverrideReason));
    setDealerType(asStr(f.cCDealerType));
    setDealerTerritory(asArray(f.cCDealerTerritoryRegions));
    setRepresentedBrands(Array.isArray(f.cCRepresentedKormaxBrands) ? (f.cCRepresentedKormaxBrands as BrandRef[]) : []);
    setExclusivity(asStr(f.cCExclusivityStatus));
    setEcomShopName(asStr(f.cCEcomShopName));
    setSalesChannels(asArray(f.cCSalesChannels));
    setPricePositioning(asStr(f.cCPricePositioning));
    setEcomAssortment(asArray(f.cCEcomAssortmentFocus));
    setActiveOutlets(typeof f.cCActiveOutletsCount === "number" ? (f.cCActiveOutletsCount as number) : 0);
    setFeedingProgram(asStr(f.cCFeedingProgram));
    setCertifications(asStr(f.cCBreederCertifications));
    setCompetitorBrand(asStr(f.cCCompetitorFeedBrand));
    setManagerComment(asStr(f.cCManagerComment));
    setPotential(asStr(f.cCPotentialEstimate));
    setDecisionMakerRole(asStr(f.cCDecisionMakerRole));
    setOrderMakerRole(asStr(f.cCOrderMakerRole));
    setAssortmentFocus(asArray(f.cCAssortmentFocus));
  }, [q.data]);

  // Reset to step 1 each time the wizard opens.
  useEffect(() => {
    if (open) {
      setStep(1);
      setToast(null);
    }
  }, [open]);

  const branch: GateBranch = primary === "breeder_professional" ? "breeder" : "commercial_non_breeder";

  // Client-side gate preview — MIRRORS backend getMissingRequiredFields exactly
  // (same mirror as the legacy quick form; the backend stays the canonical gate).
  const missing = useMemo(() => {
    const m: string[] = [];
    if (models.length === 0) m.push("Бізнес-моделі");
    if (!primary || (primary && !models.includes(primary))) m.push("Основна бізнес-модель");
    if (primary === "other" && otherNote.trim().length < 3) m.push("Опис іншої моделі");
    if (branch === "breeder") {
      if (animalTypes.length === 0) m.push("Види тварин");
      if (breeds.length === 0 && breedOther.trim().length < 2) m.push("Породи (зі списку або текстом)");
      if (!countBand) m.push("Орієнтовна кількість тварин");
    } else {
      if (!channel && !override) m.push("Основний канал");
      if (!scale) m.push("Масштаб бізнесу");
      if (!purchasing) m.push("Модель закупівель");
      if (!decision) m.push("Модель прийняття рішень");
      if (!orderFreq) m.push("Частота замовлень");
    }
    return m;
  }, [models, primary, otherNote, branch, animalTypes, breeds, breedOther, countBand, channel, override, scale, purchasing, decision, orderFreq]);

  const reviewed = q.data?.status === "manager_reviewed";

  const typeMissing = useMemo(() => {
    const t: string[] = [];
    if (models.includes("retail_chain") && activeOutlets < 3 && networkOverride.trim() === "") {
      t.push("Причина (мережа < 3 точок)");
    }
    if (models.includes("dealer_partner")) {
      if (!dealerType) t.push("Тип представництва");
      if (representedBrands.length === 0) t.push("Представлені бренди");
    }
    if (models.includes("online_store")) {
      if (salesChannels.length === 0 && !pricePositioning && ecomAssortment.length === 0) {
        t.push("E-commerce деталі (канал/ціна/асортимент)");
      }
    }
    return t;
  }, [models, activeOutlets, networkOverride, dealerType, representedBrands, salesChannels, pricePositioning, ecomAssortment]);

  const effectiveTypeMissing = reviewed ? [] : typeMissing;
  const allMissing = [...missing, ...effectiveTypeMissing];

  const aa = q.data?.allowedActions;
  const profileId = q.data?.profileId ?? null;
  const versionNumber = q.data?.versionNumber ?? null;

  function buildStepData(): QuickFormSavePayload["stepData"] {
    return {
      step1: {
        cCBusinessModels: models,
        cCPrimaryBusinessModel: primary,
        cCBusinessModelOtherNote: otherNote,
        cCPrimaryChannel: channel,
        cCManagerReviewOverrideReason: override,
      },
      step2: {
        cCBusinessScale: scale,
        cCPurchasingModel: purchasing,
        cCDecisionModel: decision,
        cCOrderFrequency: orderFreq,
        cCBreederAnimalTypes: animalTypes,
        cCBreederBreeds: breeds,
        cCBreederBreedOtherText: breedOther,
        cCBreederAnimalCountBand: countBand,
        cCNetworkType: networkType,
        cCNetworkSizeOverrideReason: networkOverride,
        cCDealerType: dealerType,
        cCDealerTerritoryRegions: dealerTerritory,
        cCRepresentedKormaxBrands: representedBrands.map((b) => ({ biBrandId: b.biBrandId, name: b.name })),
        cCExclusivityStatus: exclusivity,
        cCEcomShopName: ecomShopName,
        cCSalesChannels: salesChannels,
        cCPricePositioning: pricePositioning,
        cCEcomAssortmentFocus: ecomAssortment,
        // Wizard step-3 enrichment (whitelisted quick-form fields).
        // Trap-24 family: these enums have NO '' option — OMIT the key when empty
        // (an explicit "" fails backend validation with validation_failed).
        cCFeedingProgram: feedingProgram,
        cCBreederCertifications: certifications,
        // v1.4: значення НЕ чиститься при зміні схеми (ховаємо, не видаляємо мовчки).
        cCCompetitorFeedBrand: competitorBrand,
        cCManagerComment: managerComment,
        ...(decisionMakerRole ? { cCDecisionMakerRole: decisionMakerRole } : {}),
        ...(orderMakerRole ? { cCOrderMakerRole: orderMakerRole } : {}),
        cCAssortmentFocus: assortmentFocus,
      },
      // v1.4 fix: cCPotentialEstimate whitelisted у step3 — у step2 він мовчки
      // дропався сервером (потенціал з wizard ніколи не зберігався).
      step3: {
        ...(potential ? { cCPotentialEstimate: potential } : {}),
      },
    };
  }

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["businessProfile", holdingId] });
    await qc.invalidateQueries({ queryKey: ["client360Profile", holdingId] });
    await qc.invalidateQueries({ queryKey: ["client360", holdingId] });
    // FIX C — header-chips на overview (client360Overview) теж backend-owned.
    await qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
  };

  const draftM = useMutation({
    mutationFn: () => {
      if (!profileId || versionNumber == null) {
        return Promise.reject(new Error("Профіль не створено — збереження недоступне"));
      }
      return quickFormSave(profileId, { versionNumber, stepData: buildStepData(), action: "save_draft" });
    },
    onSuccess: async () => {
      // MP3 — explicit success-state, the wizard does NOT close silently.
      setToast({ kind: "ok", msg: "Чернетку збережено ✓" });
      await invalidate();
    },
    onError: (e: unknown) => setToast({ kind: "err", msg: saveErr(e) }),
  });

  const submitM = useMutation({
    mutationFn: async () => {
      if (!profileId || versionNumber == null) {
        throw new Error("Профіль не створено — подання недоступне");
      }
      // save → refetch fresh version → lifecycle submit (verify stays S/A).
      await quickFormSave(profileId, { versionNumber, stepData: buildStepData(), action: "save_draft" });
      const fresh = await fetchBusinessProfile(holdingId);
      await profileTransition(holdingId, "submit", {
        profileId,
        versionNumber: fresh.versionNumber ?? 0,
      });
    },
    onSuccess: async () => {
      // BUG1 — successful submit closes the drawer and returns to the refreshed
      // view-summary (needs_review state + toast shown by the parent).
      await invalidate();
      onClose();
      onSubmitted?.();
    },
    onError: (e: unknown) => setToast({ kind: "err", msg: saveErr(e) }),
  });

  const busy = draftM.isPending || submitM.isPending;
  const submitBlocked = allMissing.length > 0;

  if (!open) return null;

  const ctxModel = primary ? (M_LABEL[primary] ?? primary) : "—";
  const stepTitle = ["", "Гілка профілю", "Обов'язкові дані", "Додатково (необов'язково)", "Перевірка та збереження"][step];

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} role="dialog" aria-modal="true" aria-label="Редагування профілю">
      <div
        className="absolute inset-y-0 right-0 flex w-full max-w-[480px] flex-col bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header: context + progress (§2 — visible on every step) */}
        <div className="border-b px-5 py-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[15px] font-bold">Редагування профілю</h2>
            <button onClick={onClose} aria-label="Закрити" className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">
            {displayName} · <b className="text-foreground">{RECORD_TYPE_LABEL[recordType] ?? recordType}</b> · {ctxModel} · Крок {step} з 4
          </div>
          <div className="mt-2.5 flex gap-1.5" aria-hidden>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={cn("h-1.5 flex-1 rounded-full", i < step ? "bg-primary/70" : i === step ? "bg-primary" : "bg-muted")} />
            ))}
          </div>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wide text-primary">Крок {step} · {stepTitle}</div>

          {q.isPending && <div className="h-40 animate-pulse rounded-lg bg-muted" />}
          {q.isError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              Не вдалося завантажити профіль. <button onClick={() => q.refetch()} className="font-semibold underline">Повторити</button>
            </div>
          )}

          {q.data && (
            <>
              {/* ── STEP 1 — branch confirm (класифікація НЕ редагується тут) ── */}
              {step === 1 && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
                    Ваша гілка профілю: <b className="text-primary">{branch === "breeder" ? "Заводчик" : "Комерційний"}</b>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {branch === "breeder"
                        ? "визначає, які поля заповнюємо далі (без B2B-точок / ролей / кредитної логіки)."
                        : "визначає, які поля заповнюємо далі (комерційний набір §4)."}
                    </div>
                    <button
                      type="button"
                      onClick={onOpenClassification}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      <Tags className="h-3.5 w-3.5" /> Змінити класифікацію →
                    </button>
                  </div>
                  {/* MP5 — change-classification warning (data kept / audited). */}
                  <div className="rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-[11.5px] text-warning-foreground">
                    ⚠ Зміна гілки може змінити набір обов'язкових полів. Попередні дані профілю
                    зберігаються (audited) і не видаляються мовчки.
                  </div>
                  <p className="text-[11px] text-muted-foreground">Класифікація редагується окремо (не в profile-wizard).</p>
                </div>
              )}

              {/* ── STEP 2 — required, type-aware (ЯДРО §4) ── */}
              {step === 2 && branch === "breeder" && (
                <div className="space-y-4">
                  <Field label="Види тварин" req>
                    <div className="flex gap-1.5">
                      {ANIMAL_TYPES.map(([k, label]) => (
                        <Chip key={k} on={animalTypes.includes(k)} onClick={() => setAnimalTypes((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k])}>
                          <Dog className="mr-1 inline h-3 w-3" />{label}
                        </Chip>
                      ))}
                    </div>
                  </Field>
                  <BreedSearchPicker
                    animalTypes={animalTypes}
                    selected={breeds}
                    onChange={setBreeds}
                    other={breedOther}
                    onOther={setBreedOther}
                  />
                  <Field label="Орієнтовна кількість тварин (поголів'я)" req>
                    <Select value={countBand} onChange={setCountBand} options={[["", "—"], ...COUNT_BAND]} />
                  </Field>
                  <div className="rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                    Розплідник: <b>{asStr(q.data.fields.cCBreederKennelNamePrimary) || "—"}</b> (read-only — джерело
                    enrichment/1С). Без B2B-полів: точки · ролі · карта · кредитна логіка.
                  </div>
                </div>
              )}

              {step === 2 && branch === "commercial_non_breeder" && (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Масштаб бізнесу" req><Select value={scale} onChange={setScale} options={[["", "—"], ...SCALE]} /></Field>
                    <Field label="Модель закупівель" req><Select value={purchasing} onChange={setPurchasing} options={[["", "—"], ...PURCHASING]} /></Field>
                    <Field label="Модель рішень" req><Select value={decision} onChange={setDecision} options={[["", "—"], ...DECISION]} /></Field>
                    <Field label="Основний канал" req><Select value={channel} onChange={setChannel} options={[["", "—"], ...CHANNEL]} /></Field>
                    <Field label="Частота замовлень" req><Select value={orderFreq} onChange={setOrderFreq} options={[["", "—"], ...ORDER_FREQ]} /></Field>
                  </div>
                  {/* §4 MP4 — decision/order контакти + асортимент-фокус (B2B set). */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Хто приймає рішення (роль)"><Select value={decisionMakerRole} onChange={setDecisionMakerRole} options={[["", "—"], ...DECISION_MAKER_ROLE]} /></Field>
                    <Field label="Хто робить замовлення (роль)"><Select value={orderMakerRole} onChange={setOrderMakerRole} options={[["", "—"], ...ORDER_MAKER_ROLE]} /></Field>
                  </div>
                  <Field label="Асортимент-фокус">
                    <ChipMulti options={ASSORTMENT} selected={assortmentFocus} onToggle={(v) => setAssortmentFocus((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} scroll />
                  </Field>
                  <Field label="Або причина-override (якщо каналу немає)">
                    <input value={override} onChange={(e) => setOverride(e.target.value)} placeholder="Напр.: канал не застосовний" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
                  </Field>

                  {models.includes("retail_chain") && (
                    <div className="rounded-lg border p-3">
                      <div className="mb-2 text-xs font-semibold">Мережа</div>
                      <Field label="Тип мережі"><Select value={networkType} onChange={setNetworkType} options={[["", "—"], ...NETWORK_TYPE]} /></Field>
                      {activeOutlets < 3 && (
                        <Field label="Причина (мережа < 3 точок)" req hint={`Активних точок: ${activeOutlets}.`} className="mt-2">
                          <input value={networkOverride} onChange={(e) => setNetworkOverride(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
                        </Field>
                      )}
                    </div>
                  )}
                  {models.includes("dealer_partner") && (
                    <div className="rounded-lg border p-3">
                      <div className="mb-2 text-xs font-semibold">Дилер / представник</div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Тип представництва" req><Select value={dealerType} onChange={setDealerType} options={[["", "—"], ...DEALER_TYPE]} /></Field>
                        <Field label="Ексклюзивність"><Select value={exclusivity} onChange={setExclusivity} options={[["", "—"], ...EXCLUSIVITY]} /></Field>
                      </div>
                      <Field label="Території" className="mt-2">
                        <ChipMulti options={OBLAST.map((o): [string, string] => [o, o])} selected={dealerTerritory} onToggle={(v) => setDealerTerritory((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} scroll />
                      </Field>
                      <BrandsPicker selected={representedBrands} onChange={setRepresentedBrands} />
                    </div>
                  )}
                  {models.includes("online_store") && (
                    <div className="rounded-lg border p-3">
                      <div className="mb-2 text-xs font-semibold">E-commerce</div>
                      <Field label="Назва інтернет-магазину">
                        <input value={ecomShopName} onChange={(e) => setEcomShopName(e.target.value)} maxLength={255} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
                      </Field>
                      <Field label="Канали продажу" className="mt-2">
                        <ChipMulti options={SALES_CHANNELS} selected={salesChannels} onToggle={(v) => setSalesChannels((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} />
                      </Field>
                      <Field label="Цінове позиціювання" className="mt-2">
                        <Select value={pricePositioning} onChange={setPricePositioning} options={[["", "—"], ...PRICE_POSITIONING]} />
                      </Field>
                      <Field label="Асортимент (онлайн)" className="mt-2">
                        <ChipMulti options={ASSORTMENT} selected={ecomAssortment} onToggle={(v) => setEcomAssortment((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v])} scroll />
                      </Field>
                    </div>
                  )}

                  {/* business-models editing stays available for the commercial branch
                      (it feeds the type-conditional blocks above) */}
                  <Field label="Бізнес-моделі (впливають на додаткові блоки)">
                    <div className="flex flex-wrap gap-1.5">
                      {BUSINESS_MODELS.map(([k, label]) => (
                        <Chip key={k} on={models.includes(k)} onClick={() => setModels((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k])}>{label}</Chip>
                      ))}
                    </div>
                  </Field>
                  {primary === "other" && (
                    <Field label="Опис іншої моделі" req hint="Мінімум 3 символи">
                      <input value={otherNote} onChange={(e) => setOtherNote(e.target.value)} maxLength={500} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
                    </Field>
                  )}
                </div>
              )}

              {/* ── STEP 3 — optional / enrichment ── */}
              {step === 3 && (
                <div className="space-y-4">
                  {branch === "breeder" && (
                    <>
                      {/* v1.4: rename + select (B8: жодного «потрібно для подання» на optional-кроці) */}
                      <Field label="Клуб / система реєстрації" hint="необов'язково">
                        <Select value={certifications} onChange={setCertifications} options={[["", "—"], ...withLegacy(CLUB_REGISTRY, certifications)]} />
                      </Field>
                      <Field label="Поточна схема годування" hint="необов'язково">
                        <Select value={feedingProgram} onChange={setFeedingProgram} options={[["", "—"], ...withLegacy(FEEDING_SCHEME, feedingProgram)]} />
                      </Field>
                      {/* v1.4 microcopy — розмежування з auto-BI «купує у нас» (PB-2) */}
                      <div className="rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                        BI показує покупки у нас. Це поле описує фактичну схему годування,
                        включно з тим, що менеджер знає поза покупками.
                      </div>
                      {FEEDING_COMPETITOR_TRIGGER.has(feedingProgram) && (
                        <Field label="Конкурентний бренд" hint="яким кормом годує поза Kormax (BI цього не знає)">
                          <input value={competitorBrand} onChange={(e) => setCompetitorBrand(e.target.value)} maxLength={255} placeholder="Напр.: Royal Canin" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
                        </Field>
                      )}
                    </>
                  )}
                  <Field label="Потенціал">
                    <Select value={potential} onChange={setPotential} options={[["", "—"], ...POTENTIAL]} />
                  </Field>
                  <Field label="Коментар менеджера" hint="необов'язково">
                    <textarea value={managerComment} onChange={(e) => setManagerComment(e.target.value)} rows={3} placeholder="нотатки…" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  </Field>
                </div>
              )}

              {/* ── STEP 4 — review + save/submit ── */}
              {step === 4 && (
                <div className="space-y-1">
                  <div className="mb-2 text-xs text-muted-foreground">Що буде збережено:</div>
                  {branch === "breeder" ? (
                    <>
                      <ReviewRow k="Види тварин" v={animalTypes.map((a) => a === "dogs" ? "Собаки" : "Коти").join(" · ") || "—"} />
                      <ReviewRow k="Поголів'я" v={labelOf(COUNT_BAND, countBand)} />
                      <ReviewRow k="Породи" v={[...breeds.map((b) => b.nameUa), breedOther.trim() ? `${breedOther.trim()} (пропозиція)` : ""].filter(Boolean).join(", ") || "—"} />
                      {/* v1.4: optional-поля без warn (B8 — не тиснемо «потрібно») */}
                      <ReviewRow k="Клуб / система реєстрації" v={certifications || "—"} />
                      <ReviewRow k="Поточна схема годування" v={feedingProgram || "—"} />
                      {FEEDING_COMPETITOR_TRIGGER.has(feedingProgram) && (
                        <ReviewRow k="Конкурентний бренд" v={competitorBrand || "—"} />
                      )}
                    </>
                  ) : (
                    <>
                      <ReviewRow k="Масштаб" v={labelOf(SCALE, scale)} />
                      <ReviewRow k="Закупівлі" v={labelOf(PURCHASING, purchasing)} />
                      <ReviewRow k="Рішення" v={labelOf(DECISION, decision)} />
                      <ReviewRow k="Канал" v={labelOf(CHANNEL, channel) || override || "—"} />
                      <ReviewRow k="Частота замовлень" v={labelOf(ORDER_FREQ, orderFreq)} />
                      {assortmentFocus.length > 0 && <ReviewRow k="Асортимент-фокус" v={assortmentFocus.map((a) => labelOf(ASSORTMENT, a)).join(" · ")} />}
                    </>
                  )}
                  {potential && <ReviewRow k="Потенціал" v={labelOf(POTENTIAL, potential)} />}
                  {managerComment.trim() && <ReviewRow k="Коментар" v={managerComment.trim()} />}

                  {/* BUG1 — already-submitted state speaks for itself (not a role error). */}
                  {verifyState === "needs_review" ? (
                    <div className="mt-3 rounded-lg border border-info-foreground/20 bg-info px-3 py-2.5 text-[12.5px] text-info-foreground">
                      Вже подано на перевірку — очікує перевірки supervisor.
                      {canWithdraw && " Можна відкликати подання, щоб редагувати далі."}
                    </div>
                  ) : submitBlocked ? (
                    /* MP1 — concrete missing fields, NOT just N/M */
                    <div className="mt-3 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2.5 text-[12.5px] text-warning-foreground">
                      Щоб <b>подати на перевірку</b>, бракує: <b>{allMissing.join(" · ")}</b>.
                      Можна зберегти як чернетку зараз.
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-1.5 rounded-lg border border-success-foreground/20 bg-success px-3 py-2.5 text-[12.5px] font-medium text-success-foreground">
                      <CheckCircle2 className="h-4 w-4" /> Обов'язкові поля заповнені — можна подавати.
                    </div>
                  )}
                  {!canSubmit && verifyState !== "needs_review" && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">Подання недоступне для вашої ролі/стану профілю — збережіть чернетку.</p>
                  )}
                </div>
              )}
            </>
          )}

          {toast && (
            <div role="status" className={cn(
              "mt-4 flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm",
              toast.kind === "ok"
                ? "border-success-foreground/20 bg-success text-success-foreground"
                : "border-destructive/30 bg-destructive-soft text-destructive-soft-foreground",
            )}>
              <span>{toast.msg}</span>
              {/* BUG1 — after a successful draft save offer an explicit exit (not stuck). */}
              {toast.kind === "ok" && (
                <button
                  onClick={onClose}
                  className="shrink-0 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary-dark"
                >
                  Готово
                </button>
              )}
            </div>
          )}
        </div>

        {/* footer: Назад · Зберегти чернетку (always) · Далі / Подати */}
        <div className="flex items-center justify-between gap-2 border-t px-5 py-3">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-accent",
              step === 1 && "invisible",
            )}
          >
            <ChevronLeft className="h-4 w-4" /> Назад
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setToast(null); draftM.mutate(); }}
              disabled={!aa?.canSaveBusinessProfileDraft || busy || !profileId}
              title={!profileId ? "Профіль ще не створено" : undefined}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-40",
                step === 4 && submitBlocked
                  ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90" // MP1: draft = primary when submit blocked
                  : "bg-background hover:bg-accent",
              )}
            >
              {draftM.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Зберегти чернетку
            </button>
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Далі <ChevronRight className="h-4 w-4" />
              </button>
            ) : verifyState === "needs_review" ? (
              /* BUG1 — already submitted: offer withdraw instead of a dead submit. */
              canWithdraw ? (
                <button
                  onClick={onWithdraw}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-40"
                >
                  Відкликати подання
                </button>
              ) : null
            ) : (
              <button
                onClick={() => { setToast(null); submitM.mutate(); }}
                disabled={submitBlocked || !canSubmit || busy || !profileId}
                title={submitBlocked ? `Заповніть: ${allMissing.join(" · ")}` : !canSubmit ? "Подання недоступне (роль/стан)" : undefined}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"
              >
                {submitM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : submitBlocked || !canSubmit ? <Lock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                Подати на перевірку
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── §5 breed selector: searchable + suggestions + chips + free-text ───────── */
function BreedSearchPicker({
  animalTypes, selected, onChange, other, onOther,
}: {
  animalTypes: string[];
  selected: BreedRef[];
  onChange: (b: BreedRef[]) => void;
  other: string;
  onOther: (v: string) => void;
}) {
  const [qtext, setQtext] = useState("");
  const wantDog = animalTypes.includes("dogs");
  const wantCat = animalTypes.includes("cats");
  const dogQ = useQuery({ queryKey: ["breeds", "dog"], queryFn: ({ signal }) => fetchBreeds("dog", signal), enabled: wantDog, staleTime: 600_000 });
  const catQ = useQuery({ queryKey: ["breeds", "cat"], queryFn: ({ signal }) => fetchBreeds("cat", signal), enabled: wantCat, staleTime: 600_000 });

  const all: BreedRef[] = [
    ...(wantDog ? dogQ.data?.items ?? [] : []),
    ...(wantCat ? catQ.data?.items ?? [] : []),
  ];
  const selIds = new Set(selected.map((b) => b.id));
  const needle = qtext.trim().toLowerCase();
  const suggestions = needle.length >= 1
    ? all.filter((b) => !selIds.has(b.id)
        && (b.nameUa.toLowerCase().includes(needle) || (b.nameEn ?? "").toLowerCase().includes(needle)))
      .slice(0, 8)
    : [];
  const noMatch = needle.length >= 2 && suggestions.length === 0;

  if (animalTypes.length === 0) {
    return <p className="text-xs text-muted-foreground">Оберіть види тварин, щоб шукати породи.</p>;
  }

  return (
    <Field label="Породи" req hint="Пошук по довіднику; немає у списку → додайте як пропозицію (free-text → review)">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          value={qtext}
          onChange={(e) => setQtext(e.target.value)}
          placeholder="Пошук породи…"
          className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm"
        />
      </div>
      {(wantDog && dogQ.isPending) || (wantCat && catQ.isPending) ? (
        <div className="mt-2 h-8 animate-pulse rounded bg-muted" />
      ) : suggestions.length > 0 ? (
        <div className="mt-1.5 max-h-36 overflow-y-auto rounded-md border">
          {suggestions.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => { onChange([...selected, b]); setQtext(""); }}
              className="block w-full border-b px-3 py-1.5 text-left text-sm last:border-b-0 hover:bg-primary/5"
            >
              {b.nameUa}{b.species === "cat" ? " · кіт" : ""}
            </button>
          ))}
        </div>
      ) : noMatch ? (
        <button
          type="button"
          onClick={() => { onOther(other ? `${other}, ${qtext.trim()}` : qtext.trim()); setQtext(""); }}
          className="mt-1.5 w-full rounded-md border border-dashed px-3 py-1.5 text-left text-xs text-muted-foreground hover:border-primary hover:text-primary"
        >
          + Додати «{qtext.trim()}» як пропозицію (поза довідником)
        </button>
      ) : null}

      {(selected.length > 0 || other.trim()) && (
        <div className="mt-2">
          <div className="mb-1 text-[11px] font-medium text-muted-foreground">Обрані:</div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => onChange(selected.filter((x) => x.id !== b.id))}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/20"
                title="Прибрати"
              >
                {b.nameUa} <X className="h-3 w-3" />
              </button>
            ))}
            {other.trim() && (
              <button
                type="button"
                onClick={() => onOther("")}
                className="inline-flex items-center gap-1 rounded-full bg-warning px-2.5 py-1 text-xs font-semibold text-warning-foreground hover:bg-warning-foreground/10"
                title="Прибрати пропозицію"
              >
                {other.trim()} · пропозиція <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </Field>
  );
}

/* ── small primitives ──────────────────────────────────────────────────────── */
function ReviewRow({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-1.5 text-sm last:border-b-0">
      <span className="text-muted-foreground">{k}</span>
      <b className={cn("text-right font-semibold", warn && "text-warning-foreground")}>{v}</b>
    </div>
  );
}

function labelOf(options: Array<[string, string]>, value: string): string {
  if (!value) return "—";
  const hit = options.find(([k]) => k === value);
  return hit ? hit[1] : value;
}

function saveErr(e: unknown): string {
  if (e instanceof ApiClientError && (e.status === 409 || e.code === "profile_conflict")) {
    return "Профіль змінено іншим користувачем — оновіть і повторіть.";
  }
  return e instanceof Error ? e.message : "Помилка збереження";
}

